import {
  ChapterInfo,
  ColorPair,
  ErgRow,
  LegendEntry,
  MatrixCellData,
  MetricData,
  SiteColumn,
} from './omnidashboard.models';

export const NA_COLOR: ColorPair = { bg: '#f0f0f0', text: '#999999' };
export const NO_DATA_COLOR: ColorPair = { bg: '#e0e0e0', text: '#666666' };
export const NA_LEGEND: LegendEntry = { label: 'N/A', ...NA_COLOR };
export const NO_DATA: LegendEntry = { label: 'No Data', ...NO_DATA_COLOR };
export const ACTIVE_COLOR: ColorPair = { bg: '#d4edda', text: '#155724' };
export const ACK_COMPLETE_COLOR: ColorPair = { bg: '#c3e6cb', text: '#155724' };
export const ACK_PARTIAL_COLOR: ColorPair = { bg: '#ffeeba', text: '#856404' };
export const ACK_NOT_STARTED_COLOR: ColorPair = { bg: '#f5c6cb', text: '#721c24' };

// Annual plan status — semantically distinct hues, WCAG AA contrast, colorblind-safe
export const ANNUAL_APPROVED_COLOR: ColorPair  = { bg: '#c3e6cb', text: '#155724' }; // green  — success / complete
export const ANNUAL_SUBMITTED_COLOR: ColorPair = { bg: '#d1ecf1', text: '#0c5460' }; // teal   — submitted / awaiting review
export const ANNUAL_DRAFT_COLOR: ColorPair     = { bg: '#ffeeba', text: '#856404' }; // amber  — work in progress
export const ANNUAL_NEW_COLOR: ColorPair       = { bg: '#dbe7f3', text: '#1f3a5f' }; // blue-grey — not yet started
export const ANNUAL_REJECTED_COLOR: ColorPair  = { bg: '#f5c6cb', text: '#721c24' }; // red    — rejected

export const CHAPTER_STATUS_LEGEND: LegendEntry[] = [
  { label: 'Active', ...ACTIVE_COLOR },
  NA_LEGEND,
];

export const ACK_STATUS_LEGEND: LegendEntry[] = [
  { label: 'Complete', ...ACK_COMPLETE_COLOR },
  { label: 'In Progress', ...ACK_PARTIAL_COLOR },
  { label: 'Not Started', ...ACK_NOT_STARTED_COLOR },
  NA_LEGEND,
];

export const ANNUAL_PLAN_STATUS_LEGEND: LegendEntry[] = [
  { label: 'Approved',  ...ANNUAL_APPROVED_COLOR },
  { label: 'Submitted', ...ANNUAL_SUBMITTED_COLOR },
  { label: 'Draft',     ...ANNUAL_DRAFT_COLOR },
  { label: 'New',       ...ANNUAL_NEW_COLOR },
  { label: 'Rejected',  ...ANNUAL_REJECTED_COLOR },
  NO_DATA,
  NA_LEGEND,
];

/** Number of signatories required for a chapter to be fully acknowledged. */
export const TOTAL_SIGNATORIES = 3;

/** Fiscal year boundary: months before this value belong to the previous fiscal year. */
export const FY_START_MONTH = 4;

/** Storage keys — centralised to prevent magic-string scatter and aid testing. */
export const STORAGE_KEYS = {
  loginData: 'loginData',
  logOutFlag: 'logOutFlag',
  adminId: 'adminId',
  chapterId: 'chapterId',
  omniFiscalYear: 'omniFiscalYear',
} as const;

/** Timing constants — defer to next tick so spinner/panel can paint before sync work. */
export const OMNI_TIMING = {
  dropdownRepositionMs: 0,
  exportSpinnerMs: 0,
} as const;

export type OmniViewMode = 'chapter-status' | 'acknowledgement-status' | 'annual-plan-status';

export const NA_METRIC: MetricData = { displayValue: 'N/A', color: NA_COLOR, value: null, signedCount: 0 };

export const NA_MATRIX_CELL: MatrixCellData = {
  chapterId: 0, siteName: '', bpgName: '',
  metrics: new Map<string, MetricData>([
    ['chapter-status', NA_METRIC],
    ['acknowledgement-status', NA_METRIC],
    ['annual-plan-status', NA_METRIC],
  ])
};

export function cellKey(siteName: string, bpgName: string): string {
  return `${siteName}_${bpgName}`;
}

export function resolveVisibleSites(allSites: SiteColumn[], selectedSiteNames: string[]): SiteColumn[] {
  if (!selectedSiteNames || selectedSiteNames.length === 0) {
    return allSites;
  }
  const selectedNames = new Set(selectedSiteNames);
  return allSites.filter(site => selectedNames.has(site.siteName));
}

export function resolveVisibleErgs(allErgs: ErgRow[], selectedErgNames: string[]): ErgRow[] {
  if (!selectedErgNames || selectedErgNames.length === 0) {
    return allErgs;
  }
  const selectedNames = new Set(selectedErgNames);
  return allErgs.filter(erg => selectedNames.has(erg.bpgName));
}

export const OMNI_STRINGS = {
  pageTitle: 'Omnidashboard',
  toolbarAria: 'Matrix view and fiscal year',
  viewPillsAria: 'Matrix metric view',
  fyLabel: 'Fiscal year',
  legendAria: 'Cell color legend',
  selectAllSites: 'Select all sites',
  clearSiteSelection: 'Clear site selection',
  selectAllErgs: 'Select all ERGs',
  clearErgSelection: 'Clear ERG selection',
  exportButton: 'Export to Excel',
  exportAriaLabel: 'Export matrix to Excel spreadsheet',
  exportDisabledTitle: 'Adjust filters to export the matrix',
  genericErrorMessage: 'Something went wrong while loading omnidashboard data. Please try again.',
  emptyTitle: 'No data available',
  emptyHint:
    'No chapters are available for this view. Try another metric or contact your administrator if this seems incorrect.',
  resetFilters: 'Reset filters',
  filterRegionAria: 'Filter by site and ERG',
  sitesLabel: 'Sites',
  sitesPlaceholder: 'All Sites',
  sitesHint: 'No selection includes all sites.',
  ergsLabel: 'ERGs',
  ergsPlaceholder: 'All ERGs',
  ergsHint: 'No selection includes all ERGs.',
  filterEmptyTitle: 'No combinations match your filters',
  filterEmptyHint:
    'Broaden your site or ERG selection, or reset filters to restore the full matrix.',
  summaryFilteredHint: 'Status counts reflect the filtered view only.',
  cornerLabel: 'ERG × Site',
  announceNoMatch: 'No ERG–site combinations match your filters.',
  cellNaTooltip: 'No chapter for this ERG at this site.',
  cellOpenTooltip: 'Open chapter',
  ergSiteHeader: 'ERG \\ Site',
  clearSiteFilterAria: 'Clear site filter',
  clearErgFilterAria: 'Clear ERG filter',
};


export function omniSummaryLabel(visibleChapters: number, totalChapters: number, isFiltered: boolean): string {
  const totalLabel = `${totalChapters} ${totalChapters === 1 ? 'chapter' : 'chapters'}`;
  if (!isFiltered) {
    return 'Showing : ' + totalLabel;
  }
  return `Showing : ${visibleChapters} of ${totalLabel}`;
}

export function omniContextInView(chapterCount: number): string {
  return `${chapterCount} chapters in view`;
}

export function omniFilterContext(ergCount: number, siteCount: number): string {
  return `${ergCount} ERGs × ${siteCount} sites`;
}

export function omniGridAriaLabel(viewLabel: string, fySuffix: string): string {
  return `${viewLabel} matrix, fiscal year FY${fySuffix}`;
}

export function omniAnnounceFiltered(ergCount: number, siteCount: number, chapterCount: number): string {
  return `Filtered view: ${ergCount} ERGs, ${siteCount} sites, ${chapterCount} chapters shown.`;
}

export function omniAnnounceAll(ergCount: number, siteCount: number, chapterCount: number): string {
  return `Full matrix: ${ergCount} ERGs, ${siteCount} sites, ${chapterCount} chapters shown.`;
}

export function omniSelectionTooltipAll(count: number, plural: string): string {
  return `All ${plural} (${count})`;
}

export function omniFyChangeAnnounce(fySuffix: string): string {
  return `Loading matrix for fiscal year FY${fySuffix}.`;
}